import React, { FC, useMemo } from 'react';
import { RaceTrackView } from '@utmb/types/index';

interface RaceTrackContextProps {
    track: RaceTrackView;
    color: string;
    isDark: boolean;
    name: string;
    url3d?: string;
    gpxUrl?: string;
}

const RaceTrackContext = React.createContext({} as RaceTrackContextProps);

interface RaceTrackProviderProps {
    /** null when the race has no GPX (no track generated on the API side) */
    track?: RaceTrackView | null;
    color: string;
    isDark: boolean;
    url3d?: string;
    gpxUrl?: string;
    name: string;
}

/**
 * Track exposed to consumers when the race has no GPX.
 * Consumers (RaceTrack slice, map, profile…) rely on `points.length > 0` to decide whether to render.
 */
const EMPTY_TRACK: RaceTrackView = {
    distance: 0,
    gainElevation: 0,
    gridElevation: [],
    hasElevationMargin: false,
    lossElevation: 0,
    maxElevation: 0,
    minElevation: 0,
    points: [],
    polyline: '',
    profile: [],
    widthCoefficient: 0,
};

export const RaceTrackProvider: FC<RaceTrackProviderProps> = ({ children, track, color, name, isDark, url3d, gpxUrl }) => {
    const filteredTrack = useMemo<RaceTrackView>(() => {
        if (!track) return EMPTY_TRACK;
        return { ...track, points: (track.points ?? []).filter((pt) => pt.name !== 'HiddenLoopPoint') };
    }, [track]);

    return (
        <RaceTrackContext.Provider
            value={{
                color,
                track: filteredTrack,
                name,
                isDark,
                url3d,
                gpxUrl,
            }}
        >
            {children}
        </RaceTrackContext.Provider>
    );
};

export const useRaceTrack = () => {
    const context = React.useContext(RaceTrackContext);
    if (context === undefined) {
        throw new Error(`useRaceTrack must be used within an RaceTrackProvider`);
    }
    return context;
};
